import {
  AgentDelegationErrorCode,
  AgentDelegationKind,
  AgentDelegationStatus,
  DelegationBlockedReason,
  InboxEntryAgentDelegation,
} from '@this+that/models';

/** Mirrors apps/api's DEFAULT_ASSISTANT_TARGET_NAME
 *  (features/inboxEntries/resolvers/agentDelegationResolvers.ts) — that constant lives in
 *  the Lambda package and isn't importable from the web app, so the literal is duplicated
 *  here deliberately rather than re-derived. */
export const DEFAULT_ASSISTANT_DELEGATION_TARGET_NAME = 'Assistant';

/**
 * True for the delegation the worker auto-attaches when a message carrying a
 * `[dobox-context: ...]` prefix (the "Ask assistant about this ticket" entry point) starts
 * an AGENT_RUN (doBoxContextRunLink.ts's buildAssistantConversationDelegation) — the user is
 * just chatting with the Assistant about this ticket, already sees every reply in that chat
 * panel, and never asked to be notified about it.
 *
 * Distinct from an explicit "Delegate to agent" pick, which is also AGENT_RUN kind but always
 * carries a real `agentTemplateId` (the FE's delegate menu only offers named agent templates —
 * see useDoBoxDelegateMenu.ts's buildDelegationInputVariables) and is a genuine background task
 * the user walked away from, which still deserves a completion toast.
 */
export function isDoBoxChatLinkedDelegation(
  delegation: Pick<
    InboxEntryAgentDelegation,
    'kind' | 'targetName' | 'agentTemplateId'
  >,
): boolean {
  return (
    delegation.kind === AgentDelegationKind.AGENT_RUN &&
    delegation.agentTemplateId == null &&
    delegation.targetName === DEFAULT_ASSISTANT_DELEGATION_TARGET_NAME
  );
}

/** False for a chat-link, and for no delegation at all — the single gate every
 *  surface (trigger label, pulse dot, plan-review trigger, notifications) checks
 *  before treating `agentDelegation` as a real, user-visible delegation. */
export function isUserVisibleDelegation(
  delegation:
    | Pick<InboxEntryAgentDelegation, 'kind' | 'targetName' | 'agentTemplateId'>
    | null
    | undefined,
): boolean {
  return delegation != null && !isDoBoxChatLinkedDelegation(delegation);
}

export const DELEGATION_STATUS_LABEL_I18N_KEY: Record<
  AgentDelegationStatus,
  string
> = {
  [AgentDelegationStatus.QUEUED]: 'doBox.delegation.statusLabel.queued',
  [AgentDelegationStatus.DISPATCHING]:
    'doBox.delegation.statusLabel.dispatching',
  [AgentDelegationStatus.RUNNING]: 'doBox.delegation.statusLabel.running',
  [AgentDelegationStatus.BLOCKED]: 'doBox.delegation.statusLabel.blocked',
  [AgentDelegationStatus.PLAN_READY]: 'doBox.delegation.statusLabel.planReady',
  [AgentDelegationStatus.DONE]: 'doBox.delegation.statusLabel.done',
  [AgentDelegationStatus.FAILED]: 'doBox.delegation.statusLabel.failed',
  [AgentDelegationStatus.PAUSED]: 'doBox.delegation.statusLabel.paused',
  [AgentDelegationStatus.WAITING_FOR_DEVICE]:
    'doBox.delegation.statusLabel.waitingForDevice',
};

/** Suffixed with an AgentDelegationActivity value; the backend never sends display copy. */
export const DELEGATION_ACTIVITY_I18N_PREFIX = 'doBox.delegation.activity.';
export const DELEGATION_ACTIVITY_DONE_I18N_PREFIX =
  'doBox.delegation.activityDone.';

/** A taskType EXECUTE delegation parked BLOCKED carries a blockedReason (set by
 *  delegationFromLedger.ts, W-P3.01) that says WHY it's waiting rather than the
 *  generic DELEGATION_STATUS_LABEL_I18N_KEY.blocked copy — the ACTIVE step status
 *  label prefers this over the generic one whenever blockedReason is present. */
export const BLOCKED_REASON_I18N: Record<DelegationBlockedReason, string> = {
  [DelegationBlockedReason.NEEDS_APPROVAL]:
    'doBox.delegation.blockedReason.needsApproval',
  [DelegationBlockedReason.NEEDS_INPUT]:
    'doBox.delegation.blockedReason.needsInput',
  [DelegationBlockedReason.NEEDS_CONNECTION]:
    'doBox.delegation.blockedReason.needsConnection',
  [DelegationBlockedReason.SEND_FAILED]:
    'doBox.delegation.blockedReason.sendFailed',
  [DelegationBlockedReason.WAITING_REPLY]:
    'doBox.delegation.blockedReason.waitingReply',
};

export const DELEGATION_STATUS_VALUE_COLOR_CLASS: Record<
  AgentDelegationStatus,
  string
> = {
  [AgentDelegationStatus.QUEUED]: 'text-semantic-text-secondary',
  [AgentDelegationStatus.DISPATCHING]: 'text-semantic-text-secondary',
  [AgentDelegationStatus.RUNNING]: 'text-semantic-text-secondary',
  [AgentDelegationStatus.BLOCKED]: 'text-amber-700 dark:text-amber-300',
  [AgentDelegationStatus.PLAN_READY]: 'text-emerald-700 dark:text-emerald-300',
  [AgentDelegationStatus.DONE]: 'text-emerald-700 dark:text-emerald-300',
  [AgentDelegationStatus.FAILED]: 'text-semantic-text-danger',
  [AgentDelegationStatus.PAUSED]: 'text-amber-700 dark:text-amber-300',
  [AgentDelegationStatus.WAITING_FOR_DEVICE]:
    'text-amber-700 dark:text-amber-300',
};

export const DELEGATION_STATUS_DOT_CLASS: Record<
  AgentDelegationStatus,
  string
> = {
  [AgentDelegationStatus.QUEUED]: 'bg-semantic-text-secondary',
  [AgentDelegationStatus.DISPATCHING]: 'bg-semantic-text-secondary',
  [AgentDelegationStatus.RUNNING]: 'bg-semantic-text-secondary',
  [AgentDelegationStatus.BLOCKED]: 'bg-amber-500',
  [AgentDelegationStatus.PLAN_READY]: 'bg-emerald-500',
  [AgentDelegationStatus.DONE]: 'bg-emerald-500',
  [AgentDelegationStatus.FAILED]: 'bg-red-500',
  [AgentDelegationStatus.PAUSED]: 'bg-amber-500',
  [AgentDelegationStatus.WAITING_FOR_DEVICE]: 'bg-amber-500',
};

// DISPATCHING is the API's seconds-long claim step before a job/run exists, so it
// presents like QUEUED (incl. the in-progress spinner). WAITING_FOR_DEVICE stays out —
// it can sit for up to 72h with nothing actively running, so no spinner/elapsed timer.
export const IN_PROGRESS_DELEGATION_STATUSES = new Set<AgentDelegationStatus>([
  AgentDelegationStatus.QUEUED,
  AgentDelegationStatus.DISPATCHING,
  AgentDelegationStatus.RUNNING,
]);

// Mirrors resolveAgentPresence's AgentPresenceState.WORKING precedence (buildWorking is
// called for IN_PROGRESS_DELEGATION_STATUSES and, separately, for WAITING_FOR_DEVICE) —
// the single set a surface checks before showing "the agent is actively on this".
export const AGENT_WORKING_DELEGATION_STATUSES = new Set<AgentDelegationStatus>(
  [
    ...IN_PROGRESS_DELEGATION_STATUSES,
    AgentDelegationStatus.WAITING_FOR_DEVICE,
  ],
);

export const DISMISSIBLE_DELEGATION_STATUSES = new Set<AgentDelegationStatus>([
  AgentDelegationStatus.PLAN_READY,
  AgentDelegationStatus.DONE,
  AgentDelegationStatus.FAILED,
]);

/** Statuses where the owner may want to give up on a delegation entirely rather than
 *  act on it — a terminal failure, or a non-terminal "needs you" state (BLOCKED/
 *  PLAN_READY/PAUSED) where Pause/Resume/Stop alone don't let them close out the
 *  ticket. Gates the run view's "End run" action (clears the delegation via
 *  cancelInboxEntryDelegation), distinct from DISMISSIBLE_DELEGATION_STATUSES above,
 *  which only gates muting the notification. */
export const END_RUN_DELEGATION_STATUSES = new Set<AgentDelegationStatus>([
  AgentDelegationStatus.FAILED,
  AgentDelegationStatus.BLOCKED,
  AgentDelegationStatus.PLAN_READY,
  AgentDelegationStatus.PAUSED,
]);

/** Waiting on someone else is calm, not a warning: it takes the neutral working look. */
const WAITING_REPLY_PRESENTATION_STATUS = AgentDelegationStatus.RUNNING;

export function resolveDelegationDotClass(
  delegation: Pick<InboxEntryAgentDelegation, 'status' | 'blockedReason'>,
): string {
  return DELEGATION_STATUS_DOT_CLASS[
    isWaitingForReply(delegation)
      ? WAITING_REPLY_PRESENTATION_STATUS
      : delegation.status
  ];
}

export function isApprovalPendingBlocked(
  delegation: InboxEntryAgentDelegation,
): boolean {
  return (
    delegation.status === AgentDelegationStatus.BLOCKED &&
    delegation.pendingQuestion?.isApproval === true
  );
}

export function resolveDelegationStatusLabelI18nKey(
  status: AgentDelegationStatus,
  isApprovalPending: boolean,
  blockedReason?: DelegationBlockedReason | null,
): string {
  if (isApprovalPending) {
    return 'doBox.delegation.panel.needsDecision';
  }
  if (isWaitingForReply({ status, blockedReason })) {
    return BLOCKED_REASON_I18N[DelegationBlockedReason.WAITING_REPLY];
  }
  if (status === AgentDelegationStatus.BLOCKED) {
    return 'doBox.delegation.modal.statusLabelBlocked';
  }
  if (status === AgentDelegationStatus.PLAN_READY) {
    return 'doBox.delegation.modal.statusLabelPlanReady';
  }
  return DELEGATION_STATUS_LABEL_I18N_KEY[status];
}

const STATUS_EXPLANATION_ROOT = 'doBox.delegation.statusExplanation';

const STATUS_EXPLANATION_I18N_KEY: Record<AgentDelegationStatus, string> = {
  [AgentDelegationStatus.QUEUED]: `${STATUS_EXPLANATION_ROOT}.queued`,
  [AgentDelegationStatus.DISPATCHING]: `${STATUS_EXPLANATION_ROOT}.dispatching`,
  [AgentDelegationStatus.RUNNING]: `${STATUS_EXPLANATION_ROOT}.running`,
  [AgentDelegationStatus.BLOCKED]: `${STATUS_EXPLANATION_ROOT}.blockedGeneric`,
  [AgentDelegationStatus.PLAN_READY]: `${STATUS_EXPLANATION_ROOT}.planReady`,
  [AgentDelegationStatus.DONE]: `${STATUS_EXPLANATION_ROOT}.done`,
  [AgentDelegationStatus.FAILED]: `${STATUS_EXPLANATION_ROOT}.failed`,
  [AgentDelegationStatus.PAUSED]: `${STATUS_EXPLANATION_ROOT}.paused`,
  [AgentDelegationStatus.WAITING_FOR_DEVICE]: `${STATUS_EXPLANATION_ROOT}.waitingForDevice`,
};

const BLOCKED_EXPLANATION_I18N_KEY: Record<DelegationBlockedReason, string> = {
  [DelegationBlockedReason.NEEDS_APPROVAL]: `${STATUS_EXPLANATION_ROOT}.blockedNeedsApproval`,
  [DelegationBlockedReason.NEEDS_INPUT]: `${STATUS_EXPLANATION_ROOT}.blockedNeedsInput`,
  [DelegationBlockedReason.NEEDS_CONNECTION]: `${STATUS_EXPLANATION_ROOT}.blockedNeedsConnection`,
  [DelegationBlockedReason.SEND_FAILED]: `${STATUS_EXPLANATION_ROOT}.blockedActionFailed`,
  [DelegationBlockedReason.WAITING_REPLY]: `${STATUS_EXPLANATION_ROOT}.blockedWaitingReply`,
};

const BLOCKED_STOPPED_EARLY_I18N_KEY = `${STATUS_EXPLANATION_ROOT}.blockedStoppedEarly`;

export const NEEDS_APPROVAL_COUNT_LABEL_I18N_KEY =
  'doBox.delegation.runView.statusNeedsApproval';

/** BLOCKED on someone outside the workspace: the owner has nothing to do until they answer. */
export function isWaitingForReply(delegation: {
  status: AgentDelegationStatus;
  blockedReason?: DelegationBlockedReason | null;
}): boolean {
  return (
    delegation.status === AgentDelegationStatus.BLOCKED &&
    delegation.blockedReason === DelegationBlockedReason.WAITING_REPLY
  );
}

/** One plain sentence under the status pill: what happened and what the owner can do.
 *  A SEND_FAILED block points at a card, so it only says so while a failed card is on screen.
 *  Total over AgentDelegationStatus, so no status renders as a bare label. */
export function resolveDelegationStatusExplanationI18nKey(
  delegation: Pick<InboxEntryAgentDelegation, 'status' | 'blockedReason'>,
  isApprovalPending: boolean,
  hasFailedActionCard = true,
): string {
  if (isApprovalPending) {
    return BLOCKED_EXPLANATION_I18N_KEY[DelegationBlockedReason.NEEDS_APPROVAL];
  }
  if (
    delegation.status === AgentDelegationStatus.BLOCKED &&
    delegation.blockedReason === DelegationBlockedReason.SEND_FAILED &&
    !hasFailedActionCard
  ) {
    return BLOCKED_STOPPED_EARLY_I18N_KEY;
  }
  if (
    delegation.status === AgentDelegationStatus.BLOCKED &&
    delegation.blockedReason != null
  ) {
    return (
      BLOCKED_EXPLANATION_I18N_KEY[delegation.blockedReason] ??
      STATUS_EXPLANATION_I18N_KEY[AgentDelegationStatus.BLOCKED]
    );
  }
  return STATUS_EXPLANATION_I18N_KEY[delegation.status];
}

export interface DelegationErrorGuidanceI18nKeys {
  titleKey: string;
  actionKey: string;
}

const ERROR_GUIDANCE_I18N_KEYS_BY_CODE: Partial<
  Record<AgentDelegationErrorCode, DelegationErrorGuidanceI18nKeys>
> = {
  [AgentDelegationErrorCode.BRANCH_EXISTS]: {
    titleKey: 'doBox.delegation.errorGuidance.branchExists.title',
    actionKey: 'doBox.delegation.errorGuidance.branchExists.action',
  },
  [AgentDelegationErrorCode.NEEDS_REPO_ACCESS]: {
    titleKey: 'doBox.delegation.errorGuidance.needsRepoAccess.title',
    actionKey: 'doBox.delegation.errorGuidance.needsRepoAccess.action',
  },
  [AgentDelegationErrorCode.TIMEOUT]: {
    titleKey: 'doBox.delegation.errorGuidance.timeout.title',
    actionKey: 'doBox.delegation.errorGuidance.timeout.action',
  },
  [AgentDelegationErrorCode.MAX_TURNS]: {
    titleKey: 'doBox.delegation.errorGuidance.maxTurns.title',
    actionKey: 'doBox.delegation.errorGuidance.maxTurns.action',
  },
  [AgentDelegationErrorCode.NO_SESSION_TO_RESUME]: {
    titleKey: 'doBox.delegation.errorGuidance.noSessionToResume.title',
    actionKey: 'doBox.delegation.errorGuidance.noSessionToResume.action',
  },
  [AgentDelegationErrorCode.LOCAL_AGENT_UNREACHABLE]: {
    titleKey: 'doBox.delegation.errorGuidance.localAgentUnreachable.title',
    actionKey: 'doBox.delegation.errorGuidance.localAgentUnreachable.action',
  },
  [AgentDelegationErrorCode.PERMISSION_POLICY_BLOCKED]: {
    titleKey: 'doBox.delegation.errorGuidance.permissionPolicyBlocked.title',
    actionKey: 'doBox.delegation.errorGuidance.permissionPolicyBlocked.action',
  },
  [AgentDelegationErrorCode.PR_DESCRIPTION_UPDATE_FAILED]: {
    titleKey: 'doBox.delegation.errorGuidance.prDescriptionUpdateFailed.title',
    actionKey:
      'doBox.delegation.errorGuidance.prDescriptionUpdateFailed.action',
  },
  [AgentDelegationErrorCode.NO_CHANGES]: {
    titleKey: 'doBox.delegation.errorGuidance.noChanges.title',
    actionKey: 'doBox.delegation.errorGuidance.noChanges.action',
  },
  [AgentDelegationErrorCode.NO_STEP_ATTEMPTED]: {
    titleKey: 'doBox.delegation.errorGuidance.noStepAttempted.title',
    actionKey: 'doBox.delegation.errorGuidance.noStepAttempted.action',
  },
  [AgentDelegationErrorCode.NO_REVIEW_COMMENTS]: {
    titleKey: 'doBox.delegation.errorGuidance.noReviewComments.title',
    actionKey: 'doBox.delegation.errorGuidance.noReviewComments.action',
  },
  [AgentDelegationErrorCode.CONNECTION_NOT_FOUND]: {
    titleKey: 'doBox.delegation.errorGuidance.connectionNotFound.title',
    actionKey: 'doBox.delegation.errorGuidance.connectionNotFound.action',
  },
  [AgentDelegationErrorCode.LOCAL_JOB_NOT_FOUND]: {
    titleKey: 'doBox.delegation.errorGuidance.localJobNotFound.title',
    actionKey: 'doBox.delegation.errorGuidance.localJobNotFound.action',
  },
  [AgentDelegationErrorCode.DEVICE_OFFLINE_EXPIRED]: {
    titleKey: 'doBox.delegation.errorGuidance.deviceOfflineExpired.title',
    actionKey: 'doBox.delegation.errorGuidance.deviceOfflineExpired.action',
  },
  [AgentDelegationErrorCode.SOURCE_CHANGED_BEFORE_DISPATCH]: {
    titleKey:
      'doBox.delegation.errorGuidance.sourceChangedBeforeDispatch.title',
    actionKey:
      'doBox.delegation.errorGuidance.sourceChangedBeforeDispatch.action',
  },
  [AgentDelegationErrorCode.DISPATCH_UNCONFIRMED]: {
    titleKey: 'doBox.delegation.errorGuidance.dispatchUnconfirmed.title',
    actionKey: 'doBox.delegation.errorGuidance.dispatchUnconfirmed.action',
  },
};

const UNKNOWN_ERROR_GUIDANCE_I18N_KEYS: DelegationErrorGuidanceI18nKeys = {
  titleKey: 'doBox.delegation.errorGuidance.unknown.title',
  actionKey: 'doBox.delegation.errorGuidance.unknown.action',
};

export function resolveDelegationErrorGuidanceI18nKeys(
  errorCode?: AgentDelegationErrorCode,
): DelegationErrorGuidanceI18nKeys {
  if (!errorCode) {
    return UNKNOWN_ERROR_GUIDANCE_I18N_KEYS;
  }
  return (
    ERROR_GUIDANCE_I18N_KEYS_BY_CODE[errorCode] ??
    UNKNOWN_ERROR_GUIDANCE_I18N_KEYS
  );
}
